import { ref, nextTick, onMounted, onUnmounted, watch, type Ref, type MaybeRef, toRef } from 'vue';
import { useNuxtApp } from 'nuxt/app';
import { PUSH_VIEW_BANNER } from '~~/common/plugins/aw-analytics';
import type { PromotionSchema } from '~~/common/utils/AnalyticsBannerInteraction';

interface BannerItem {
  id: string;
  campaignName?: string | null;
  promotionBannerIndex?: string | number | null;
}

export interface UseBannerInteractionAnalyticsOptions {
  componentEl: Ref<HTMLElement | null>;
  banners: Ref<BannerItem[]>;
  analyticsBannerInteraction: MaybeRef<PromotionSchema | null>;
  analyticsMeasurable?: MaybeRef<boolean>;
}

export function useBannerInteractionAnalytics ({
  componentEl,
  banners,
  analyticsBannerInteraction: analyticsBannerInteractionOption,
  analyticsMeasurable: analyticsMeasurableOption = false,
}: UseBannerInteractionAnalyticsOptions) {
  const analyticsBannerInteraction = toRef(analyticsBannerInteractionOption);
  const analyticsMeasurable = toRef(analyticsMeasurableOption);
  const { $awAnalytics } = useNuxtApp();
  const viewedBannerIds = ref(new Set<string>());
  let intersectionObserver: IntersectionObserver | null = null;

  function sendViewBannerAnalytics (banner: BannerItem, bannerIndex: number) {
    if (!analyticsBannerInteraction.value || !analyticsMeasurable.value) {
      return;
    }

    const interaction = {
      ...analyticsBannerInteraction.value,
      promotionName: banner.campaignName || null,
      promotionBannerIndex: banner.promotionBannerIndex ?? bannerIndex + 1,
    };

    $awAnalytics[PUSH_VIEW_BANNER]({ analyticsBannerInteraction: interaction });
  }

  function setupIntersectionObserver () {
    if (import.meta.server) {
      return;
    }

    if (intersectionObserver) {
      intersectionObserver.disconnect();
    }

    intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const bannerCard = entry.target;
          const bannerId = bannerCard.getAttribute('data-banner-id');
          const bannerIndex = bannerCard.getAttribute('data-banner-index');

          if (bannerId && !viewedBannerIds.value.has(bannerId)) {
            viewedBannerIds.value.add(bannerId);

            const banner = banners.value.find((b) => b?.id?.toString() === bannerId);

            if (banner) {
              sendViewBannerAnalytics(banner, bannerIndex ? parseInt(bannerIndex, 10) : 0);
            }
          }
        }
      });
    }, {
      root: null,
      rootMargin: '0px',
      threshold: 0.5,
    });

    nextTick(() => {
      observeBannerCards();
    });
  }

  function observeBannerCards () {
    if (!intersectionObserver || !componentEl.value) {
      return;
    }

    const bannerCards = componentEl.value.querySelectorAll('[data-banner-id]');
    bannerCards.forEach((card) => {
      intersectionObserver!.observe(card);
    });
  }

  function getGlobalBannerIndex (banner: BannerItem): number {
    // Compared as strings because callers pass the raw API item, whose `id` may still be a number
    // while `banners` holds it stringified to satisfy `BannerItem`.
    const index = banners.value.findIndex((b) => b?.id?.toString() === banner?.id?.toString());
    return index !== -1 ? index : 0;
  }

  function extendAnalyticsBannerInteraction (banner: BannerItem) {
    if (!analyticsBannerInteraction.value) {
      return null;
    }

    const resolvedBanner = banners.value.find((b) => b?.id?.toString() === banner?.id?.toString()) ?? banner;
    const globalIndex = getGlobalBannerIndex(banner);
    return {
      ...analyticsBannerInteraction.value,
      promotionName: resolvedBanner?.campaignName || null,
      promotionBannerIndex: resolvedBanner?.promotionBannerIndex ?? globalIndex + 1,
    };
  }

  onMounted(() => {
    nextTick(() => {
      nextTick(() => {
        setupIntersectionObserver();
      });
    });
  });

  onUnmounted(() => {
    if (intersectionObserver) {
      intersectionObserver.disconnect();
      intersectionObserver = null;
    }
  });

  watch(() => componentEl.value, (newVal) => {
    if (newVal && !intersectionObserver) {
      setupIntersectionObserver();
    }
  });

  watch(() => banners.value, () => {
    nextTick(() => {
      observeBannerCards();
    });
  }, { deep: true });

  return {
    viewedBannerIds,
    getGlobalBannerIndex,
    extendAnalyticsBannerInteraction,
    observeBannerCards,
  };
}
